import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { uiTheme as t, FormDrawer, SelectField } from 'Components/ui';
import styled from '@emotion/styled';
import useTranslation from 'Hooks/useTranslation';
import employeesApi from 'Api/axios/employees-api';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  employeeId: string;
  /** Identificador del rol que ya tiene, para no ofrecer guardar lo mismo. */
  currentRoleId: string;
  roles: Array<{ id: string; name: string }>;
  onClose: () => void;
  /** Refresca la ficha: el rol cambió y la métrica debe reflejarlo. */
  onSaved: () => void;
}

const FieldText = styled.span({ fontFamily: t.font, fontSize: 13, fontWeight: 600, color: t.color.title });

const Field = styled.label({ display: 'flex', flexDirection: 'column', gap: 6 });

/**
 * Cambio de tipo de usuario, en el cajón lateral.
 *
 * La lista de roles llega por prop (la página la carga con `rolesApi`) y el
 * guardado habla con `PATCH /employees/:id/role`. Si quien confirma no cambió
 * el rol, no se llama al backend: solo se avisa.
 */
const ChangeRoleDrawer = ({ open, employeeId, currentRoleId, roles, onClose, onSaved }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const [selectedRoleId, setSelectedRoleId] = useState(currentRoleId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedRoleId(currentRoleId);
      setError(null);
    }
  }, [open, currentRoleId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedRoleId || selectedRoleId === currentRoleId) {
      setError(t2('AlertNotNewRole') as string);
      return;
    }

    setSaving(true);
    const response = await employeesApi.changeRole(employeeId, selectedRoleId);
    setSaving(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    toast.success(t2('AlertSuccessChangeRole') as string);
    onSaved();
    onClose();
  };

  return (
    <FormDrawer
      open={open}
      formId="change-role-form"
      title={t2('ChangeRole') as string}
      subtitle={t2('change_user_type_subtitle') as string}
      labels={{ close: t2('Close') as string, cancel: t2('Cancel') as string, submit: t2('Accept') as string }}
      submitting={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Field>
        <FieldText>{t2('UserType')}</FieldText>
        <SelectField
          id="role"
          value={selectedRoleId}
          onChange={value => {
            setSelectedRoleId(value);
            setError(null);
          }}
          options={roles.map(role => ({ value: role.id, label: role.name }))}
          error={error ?? undefined}
          aria-label={t2('ChangeRole') as string}
        />
      </Field>
    </FormDrawer>
  );
};

export default ChangeRoleDrawer;
